import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Heart, ArrowRight, XCircle } from "lucide-react";
import { getPaymentStatus } from "../lib/api";

const MAX_POLLS = 8;

const PaymentStatus = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [info, setInfo] = useState(null);

  useEffect(() => {
    document.title = "Thank you | Iron Shield Veterans Reset Center";
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const data = await getPaymentStatus(sessionId);
        setInfo(data);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (data.payment_status === "failed" || data.payment_status === "expired") { setState("error"); return; }
        attempts += 1;
        if (attempts >= MAX_POLLS) { setState("pending"); return; }
        timer = setTimeout(poll, 2000);
      } catch (e) {
        attempts += 1;
        if (attempts >= MAX_POLLS) { setState("error"); return; }
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <section className="min-h-[70vh] flex items-center justify-center bg-offwhite pt-[140px] pb-24" data-testid="payment-status">
      <div className="container-x max-w-xl">
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="i-card bg-white p-8 md:p-12 text-center"
        >
          {state === "checking" && (
            <>
              <Loader2 className="h-12 w-12 text-navy animate-spin mx-auto" />
              <h1 className="mt-6 font-heading text-2xl md:text-3xl font-black text-navy">Confirming your donation…</h1>
              <p className="mt-3 text-charcoal/75">One moment while we verify your payment securely.</p>
            </>
          )}

          {state === "paid" && (
            <>
              <div className="h-16 w-16 rounded-full bg-teal/10 flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-9 w-9 text-teal" />
              </div>
              <h1 className="mt-6 font-heading text-3xl md:text-4xl font-black text-navy" data-testid="payment-success-title">Thank you for standing with veterans.</h1>
              <p className="mt-4 text-charcoal/80 leading-relaxed">
                Your {info?.frequency === "monthly" ? "monthly" : "one-time"} gift{info?.amount ? ` of $${info.amount}` : ""} is confirmed. It goes straight to housing, stabilization, and reset support for a veteran in crisis.
              </p>
              {info?.frequency === "monthly" && info?.manage_token && (
                <div className="mt-6 rounded-lg border border-navy/20 bg-offwhite p-5 text-left" data-testid="manage-link-box">
                  <div className="label-caps text-navy">Manage your monthly gift</div>
                  <p className="mt-2 text-sm text-charcoal/70">Bookmark your private link to update the amount, pause, or cancel any time — no account needed.</p>
                  <Link to={`/manage/${info.manage_token}`} data-testid="success-manage-link" className="mt-4 inline-flex items-center justify-center gap-2 bg-navy text-offwhite px-6 py-3 rounded-lg text-sm font-semibold hover:bg-navy-deep transition-colors">
                    Manage my gift
                  </Link>
                </div>
              )}
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/" className="inline-flex items-center justify-center gap-2 bg-navy text-offwhite px-7 py-3.5 rounded-md text-sm font-semibold hover:bg-navy-deep transition-colors" data-testid="payment-home-btn">
                  Back to home
                </Link>
                <Link to="/programs" className="group inline-flex items-center justify-center gap-2 border border-navy text-navy px-7 py-3.5 rounded-md text-sm font-semibold hover:bg-navy hover:text-offwhite transition-colors">
                  See the impact <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </>
          )}

          {state === "pending" && (
            <>
              <Heart className="h-12 w-12 text-gold mx-auto" />
              <h1 className="mt-6 font-heading text-2xl md:text-3xl font-black text-navy">Your donation is processing.</h1>
              <p className="mt-3 text-charcoal/75">It's taking a little longer than usual to confirm. You'll receive an email receipt from Stripe shortly.</p>
              <Link to="/" className="mt-8 inline-flex items-center justify-center gap-2 bg-navy text-offwhite px-7 py-3.5 rounded-md text-sm font-semibold hover:bg-navy-deep transition-colors">Back to home</Link>
            </>
          )}

          {state === "error" && (
            <>
              <XCircle className="h-12 w-12 text-slate mx-auto" />
              <h1 className="mt-6 font-heading text-2xl md:text-3xl font-black text-navy">We couldn't confirm this donation.</h1>
              <p className="mt-3 text-charcoal/75">If you were charged, you'll still get a receipt. Otherwise, please try again or contact us.</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/donate" className="inline-flex items-center justify-center gap-2 bg-navy text-offwhite px-7 py-3.5 rounded-md text-sm font-semibold hover:bg-navy-deep transition-colors">Try again</Link>
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 border border-navy text-navy px-7 py-3.5 rounded-md text-sm font-semibold hover:bg-navy hover:text-offwhite transition-colors">Contact us</Link>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default PaymentStatus;
